import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Overlay,
  Dialog,
  Header,
  Title,
  Desc,
  Actions,
  Button,
  ConfirmLabel,
  ConfirmInput,
  ApiError,
} from "./styles";
import { ftpAPI } from "api";
import { useTaskStore } from "store/task";
import { openGlobalError } from "modal/error/errorModalStore";

const DeleteFtpUser = ({ open, setOpen }) => {
  const { addTask, tasks } = useTaskStore();
  const [targetUsername, setTargetUsername] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [apiErr, setApiErr] = useState<string | null>(null);
  const usernameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTargetUsername("");
    setConfirmText("");
    setApiErr(null);
    const t = setTimeout(() => usernameRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open]);

  const canSubmit = useMemo(
    () => targetUsername.trim().length > 0 && confirmText === "삭제하겠습니다",
    [targetUsername, confirmText]
  );

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const normalizedUsername = targetUsername.trim();
    const hasPendingConflict = tasks.some(
      (task) =>
        task.state === "pending" &&
        task.name?.trim() === normalizedUsername &&
        (task.job === "Create FTP User" || task.job === "Remove FTP User")
    );
    if (hasPendingConflict) {
      openGlobalError({
        title: "Task In Progress",
        message: "같은 FTP 사용자에 대한 작업이 아직 진행 중입니다.",
      });
      return;
    }

    try {
      setApiErr(null);
      const res = await ftpAPI.deleteUser(normalizedUsername);
      addTask({
        name: normalizedUsername,
        id: res?.data.task_id,
        job: `Remove FTP User`,
        state: "pending",
      });
      setOpen(false);
    } catch (err: any) {
      setApiErr(
        err?.response?.data?.detail?.message ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "유저 삭제에 실패했습니다."
      );
    }
  };

  if (!open) return null;

  return createPortal(
    <Overlay>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-user-title"
      >
        <Header>
          <Title id="delete-user-title">Delete FTP User</Title>
        </Header>

        <Desc>
          삭제할 유저 이름을 입력한 뒤, 아래에 <strong>“삭제하겠습니다”</strong>
          를 입력하면 삭제가 진행됩니다.
        </Desc>

        {apiErr && <ApiError role="alert">{String(apiErr)}</ApiError>}

        <form
          onSubmit={handleDelete}
          style={{ display: "grid", gap: 14, marginTop: 12, paddingTop: 8 }}
        >
          <ConfirmLabel>삭제할 유저 이름</ConfirmLabel>
          <ConfirmInput
            ref={usernameRef}
            value={targetUsername}
            onChange={(e) => setTargetUsername(e.target.value)}
            placeholder="예: ftp_user01"
          />

          <ConfirmLabel>확인 문구 입력</ConfirmLabel>
          <ConfirmInput
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="삭제하겠습니다"
          />

          <Actions>
            <Button
              type="button"
              onClick={() => setOpen(false)}
              data-variant="ghost"
            >
              취소
            </Button>
            <Button
              type="submit"
              data-variant="primary"
              disabled={!canSubmit}
              style={{ background: "#dc2626" }}
            >
              삭제
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default DeleteFtpUser;
